// src/hooks/useAuth.js
import { useState, useEffect } from 'react';
import {
  auth,
  googleProvider,
  facebookProvider,
  setAuthPersistence,
  logAnalyticsEvent
} from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  signInWithRedirect,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
  sendEmailVerification,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Set auth persistence to local
    setAuthPersistence('local');

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      if (firebaseUser) {
        // User is signed in
        const userData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0],
          photoURL: firebaseUser.photoURL || null,
          emailVerified: firebaseUser.emailVerified,
          phoneNumber: firebaseUser.phoneNumber || null,
          lastLogin: new Date().toISOString(),
        };
        
        // Fetch additional user data from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            const userProfile = userDoc.data();
            setUser({
              ...userData,
              ...userProfile,
              // Merge Firestore data with auth data
              username: userProfile.username || userData.displayName,
              bio: userProfile.bio || '',
              avatar: userProfile.avatar || userData.photoURL,
              role: userProfile.role || 'user',
              createdAt: userProfile.createdAt?.toDate?.() || new Date(),
            });
          } else {
            // User document doesn't exist, create it
            await createUserDocument(firebaseUser.uid, userData);
            setUser(userData);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUser(userData);
        }
        
        // Log login event
        logAnalyticsEvent('user_login', {
          method: 'email',
          user_id: firebaseUser.uid
        });
      } else {
        // User is signed out
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Create user document in Firestore
  const createUserDocument = async (uid, userData) => {
    try {
      const userRef = doc(db, 'users', uid);
      await setDoc(userRef, {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        onboardingComplete: false,
        preferences: {
          notifications: true,
          darkMode: true,
        },
        stats: {
          followers: 0,
          following: 0,
          atoms: 0,
          likes: 0,
        }
      });
      toast.success('Account created successfully!');
    } catch (error) {
      console.error('Error creating user document:', error);
      toast.error('Failed to create user profile');
    }
  };

  // Sign up with email and password
  const signUp = async (email, password, displayName) => {
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const { user } = userCredential;
      
      // Update profile with display name
      await updateProfile(user, { displayName });
      
      // Send email verification
      await sendEmailVerification(user);
      toast.success('Account created! Please verify your email.');
      
      logAnalyticsEvent('user_registered', {
        method: 'email'
      });
      
      return { user, success: true };
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
      return { error: error.message, success: false };
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    setError(null);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const { user } = userCredential;
      
      if (!user.emailVerified) {
        toast.warning('Please verify your email before signing in');
        await signOut(auth);
        return { error: 'Email not verified', success: false };
      }
      
      toast.success('Welcome back!');
      logAnalyticsEvent('login', {
        method: 'email'
      });
      
      return { user, success: true };
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
      return { error: error.message, success: false };
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    setError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const { user } = result;
      
      // Check if user document exists, create if not
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        await createUserDocument(user.uid, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          emailVerified: user.emailVerified,
        });
      }
      
      toast.success('Signed in with Google!');
      logAnalyticsEvent('login', {
        method: 'google'
      });
      
      return { user, success: true };
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
      return { error: error.message, success: false };
    }
  };

  // Sign in with Facebook
  const signInWithFacebook = async () => {
    setError(null);
    try {
      const result = await signInWithPopup(auth, facebookProvider);
      const { user } = result;
      
      // Check if user document exists, create if not
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        await createUserDocument(user.uid, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          emailVerified: user.emailVerified,
        });
      }
      
      toast.success('Signed in with Facebook!');
      logAnalyticsEvent('login', {
        method: 'facebook'
      });
      
      return { user, success: true };
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
      return { error: error.message, success: false };
    }
  };

  // Sign out
  const signOutUser = async () => {
    setError(null);
    try {
      await signOut(auth);
      toast.success('Signed out successfully');
      logAnalyticsEvent('logout');
      return { success: true };
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
      return { error: error.message, success: false };
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent!');
      return { success: true };
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
      return { error: error.message, success: false };
    }
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    setError(null);
    try {
      const { displayName, photoURL } = updates;
      const user = auth.currentUser;
      
      if (user) {
        await updateProfile(user, { displayName, photoURL });
        
        // Update Firestore
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, {
          ...updates,
          updatedAt: serverTimestamp()
        }, { merge: true });
        
        toast.success('Profile updated!');
        return { success: true };
      }
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
      return { error: error.message, success: false };
    }
  };

  // Reauthenticate user
  const reauthenticate = async (password) => {
    setError(null);
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('No user logged in');
      }
      
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      return { success: true };
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
      return { error: error.message, success: false };
    }
  };

  // Delete account
  const deleteAccount = async (password) => {
    setError(null);
    try {
      // Reauthenticate first
      const reauthResult = await reauthenticate(password);
      if (!reauthResult.success) {
        throw new Error(reauthResult.error);
      }
      
      const user = auth.currentUser;
      if (user) {
        // Delete user data from Firestore
        await setDoc(doc(db, 'users', user.uid), { 
          isActive: false,
          deletedAt: serverTimestamp()
        }, { merge: true });
        
        // Delete the user account
        await deleteUser(user);
        toast.success('Account deleted successfully');
        return { success: true };
      }
    } catch (error) {
      setError(error.message);
      toast.error(error.message);
      return { error: error.message, success: false };
    }
  };

  return {
    user,
    loading,
    error,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithFacebook,
    signOutUser,
    resetPassword,
    updateUserProfile,
    deleteAccount,
    reauthenticate,
    isAuthenticated: !!user,
  };
};